/**
 * Sync Shopify locations to app product_locations and warehouses.
 * Creates corresponding app locations so inventory sync is per-location.
 */

import { supabase } from '../lib/supabase';

export interface ShopifyLocationToSync {
  id: string;
  name: string;
  isActive: boolean;
}

export interface SyncLocationsResult {
  success: boolean;
  createdCount: number;
  productLocations: Array<{ id: string; name: string }>;
  warehouses: Array<{ id: string; name: string }>;
  mappings: Array<{ shopifyLocationId: string; productLocationId: string; warehouseId: string }>;
  error?: string;
}

/**
 * Sync Shopify locations to app: creates product_locations, warehouses, and shopify_location_mappings.
 * Each Shopify location becomes an app location (visible in Inventory) with per-location inventory.
 */
export async function syncLocationsFromShopify(
  shopifyLocations: ShopifyLocationToSync[],
  adminId: string,
  shopId: string
): Promise<SyncLocationsResult> {
  const result: SyncLocationsResult = {
    success: false,
    createdCount: 0,
    productLocations: [],
    warehouses: [],
    mappings: [],
  };

  if (!adminId || !shopId || shopifyLocations.length === 0) {
    result.error = 'Missing adminId, shopId, or locations';
    return result;
  }

  try {
    for (const loc of shopifyLocations) {
      const shopifyLocationId = parseInt(loc.id, 10);
      if (isNaN(shopifyLocationId)) continue;

      const locationName = (loc.name || 'Unknown').trim();
      if (!locationName) continue;

      // 1. Get or create product_location (for Inventory page, product_location_inventory)
      let productLocationId: string | null = null;
      const { data: existingProductLocation } = await supabase
        .from('product_locations')
        .select('id, name')
        .eq('admin_id', adminId)
        .eq('name', locationName)
        .maybeSingle();

      if (existingProductLocation) {
        productLocationId = existingProductLocation.id;
        result.productLocations.push(existingProductLocation);
      } else {
        const { data: newProductLocation, error: plError } = await supabase
          .from('product_locations')
          .insert({ admin_id: adminId, name: locationName })
          .select('id, name')
          .single();

        if (plError) {
          console.error('Failed to create product_location:', plError);
          continue;
        }
        if (newProductLocation) {
          productLocationId = newProductLocation.id;
          result.productLocations.push(newProductLocation);
        }
      }

      if (!productLocationId) continue;

      // 2. Create warehouse (for Warehouse Map, optional) - use defaults
      let warehouseId: string | null = null;
      const { data: existingWarehouse } = await supabase
        .from('warehouses')
        .select('id')
        .eq('admin_id', adminId)
        .ilike('name', locationName)
        .eq('is_archived', false)
        .maybeSingle();

      if (existingWarehouse) {
        warehouseId = existingWarehouse.id;
      } else {
        const { data: newWarehouse, error: whError } = await supabase
          .from('warehouses')
          .insert({
            admin_id: adminId,
            name: locationName,
            unit: 'm',
            length: 10,
            width: 10,
          })
          .select('id')
          .single();

        if (!whError && newWarehouse) {
          warehouseId = newWarehouse.id;
          result.warehouses.push({ id: newWarehouse.id, name: locationName });

          // Create warehouse_layout for new warehouse
          await supabase.from('warehouse_layouts').insert({
            warehouse_id: newWarehouse.id,
            grid_size: 10,
            viewport_json: { x: 0, y: 0, scale: 1 },
          });
        }
      }

      // 3. Upsert shopify_location_mapping with product_location_id and warehouse_id
      const { error: mappingError } = await supabase
        .from('shopify_location_mappings')
        .upsert(
          {
            admin_id: adminId,
            shop_id: shopId,
            shopify_location_id: shopifyLocationId,
            shopify_location_name: locationName,
            product_location_id: productLocationId,
            warehouse_id: warehouseId,
            is_primary: result.createdCount === 0,
            is_active: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'shop_id,shopify_location_id' }
        );

      if (!mappingError) {
        result.createdCount++;
        result.mappings.push({
          shopifyLocationId: loc.id,
          productLocationId,
          warehouseId: warehouseId || '',
        });
      }
    }

    result.success = result.createdCount > 0;
    return result;
  } catch (err) {
    result.error = err instanceof Error ? err.message : 'Unknown error';
    return result;
  }
}
